import { LineSeg2d } from '@/editor/math/LineSeg2d';
import {
  BOX_BOUNDING_LINE_WEIGHT,
  EAbsorbAxis,
  IAbsorbResult,
  IBoxBoundingLineInfo,
  ICenterLineInfo,
  IRotatedBox
} from './rotatedBoxAbsorbCalculator';
import { Line2d } from '@/editor/math/Line2d';
import { normalizeAngle } from '@/editor/math/utils';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { RADIAN_ANGLE_THRESHOLD } from '@/editor/constant/math';
import { getPointsBounds } from '../viewportFilter';

function getRotatedBoxPeaks(box: IRotatedBox) {
  const { center, width, thickness, rotation } = box;
  const diagonal = Math.hypot(width, thickness);
  const boxRightTopDirection = Point2dUtils.rotateDirection({ x: width / diagonal, y: thickness / diagonal }, rotation);
  const boxRightBottomDirection = Point2dUtils.rotateDirection(
    { x: width / diagonal, y: -thickness / diagonal },
    rotation
  );
  const rightTopPoint = Point2dUtils.addDistance(center, diagonal / 2, boxRightTopDirection);
  const rightBottomPoint = Point2dUtils.addDistance(center, diagonal / 2, boxRightBottomDirection);
  const leftTopPoint = Point2dUtils.addDistance(center, -diagonal / 2, boxRightBottomDirection);
  const leftBottomPoint = Point2dUtils.addDistance(center, -diagonal / 2, boxRightTopDirection);

  return [rightTopPoint, leftTopPoint, leftBottomPoint, rightBottomPoint];
}

// 处理包围盒平行边吸附线段，要转换成可直接渲染的线段
export function convertParallelAbsorbSeb(
  absorbSegInfos: IAbsorbResult['absorbSegInfos'],
  box: IRotatedBox
): LineSeg2d[] {
  const [rightTopPoint, leftTopPoint, leftBottomPoint, rightBottomPoint] = getRotatedBoxPeaks(box);

  return absorbSegInfos.map((info) => {
    if (info.absorbDistance === 0) {
      // 中心点吸附时
      if (info.axis === EAbsorbAxis.Center) {
        return new LineSeg2d(info.seg.start, box.center);
      }

      return info.seg;
    }

    const seg = info.seg;
    const leftTopFootRatio = seg.getFootRatio(leftTopPoint);
    const leftBottomFootRatio = seg.getFootRatio(leftBottomPoint);
    const rightTopFootRatio = seg.getFootRatio(rightTopPoint);
    const rightBottomFootRatio = seg.getFootRatio(rightBottomPoint);
    const footRatios = [leftTopFootRatio, leftBottomFootRatio, rightTopFootRatio, rightBottomFootRatio, 0, 1];

    return new LineSeg2d(
      seg.getPointFromParameter(Math.min(...footRatios)),
      seg.getPointFromParameter(Math.max(...footRatios))
    );
  });
}

// 角度限制在[0, Math.PI)
export function limitAngle(angle: number) {
  angle = normalizeAngle(angle);

  if (Math.abs(angle - Math.PI / 2) < RADIAN_ANGLE_THRESHOLD) return Math.PI / 2;
  if (Math.abs(angle - Math.PI) < RADIAN_ANGLE_THRESHOLD) return 0;
  if (Math.abs(angle - (3 * Math.PI) / 2) < RADIAN_ANGLE_THRESHOLD) return Math.PI / 2;
  if (Math.abs(angle - 2 * Math.PI) < RADIAN_ANGLE_THRESHOLD) return 0;

  if (angle >= Math.PI) angle -= Math.PI;

  return angle;
}

// 线角度限制在[0, Math.PI)
export function getLineAngle(line: Line2d) {
  const slope = line.getSlope();
  const angle = Math.atan(slope);
  return limitAngle(angle);
}

export function createBoxParallelAbsorbSegs(rotatedBox: IRotatedBox) {
  const centerLines: ICenterLineInfo[] = [];
  const boxBoundingLines: IBoxBoundingLineInfo[] = [];

  const [rightTopPoint, leftTopPoint, leftBottomPoint, rightBottomPoint] = getRotatedBoxPeaks(rotatedBox);
  // 视口过滤按整个box判定可见性：中心线的seg只是一段表达方向的短线段，不能代表box位置
  const bounds = getPointsBounds([rightTopPoint, leftTopPoint, leftBottomPoint, rightBottomPoint]);

  // 中心线沿box自身坐标轴，box旋转时（如贴在斜墙上）中心线跟着旋转
  const boxXDirection = Point2dUtils.rotateDirection({ x: 1, y: 0 }, rotatedBox.rotation);
  const boxYDirection = Point2dUtils.rotateDirection({ x: 0, y: 1 }, rotatedBox.rotation);

  // 添加box x轴方向中心线
  const xCenterLine = new Line2d({ parametric: { point: rotatedBox.center, direction: boxXDirection } });
  centerLines.push({ lineCenter: rotatedBox.center, line: xCenterLine, angle: getLineAngle(xCenterLine), bounds });

  // 添加box y轴方向中心线
  const yCenterLine = new Line2d({ parametric: { point: rotatedBox.center, direction: boxYDirection } });
  centerLines.push({ lineCenter: rotatedBox.center, line: yCenterLine, angle: getLineAngle(yCenterLine), bounds });

  // 添加包围盒包围线
  const topSeg = new LineSeg2d(rightTopPoint, leftTopPoint);
  const bottomSeg = new LineSeg2d(rightBottomPoint, leftBottomPoint);
  const leftSeg = new LineSeg2d(leftTopPoint, leftBottomPoint);
  const rightSeg = new LineSeg2d(rightTopPoint, rightBottomPoint);

  const topLine = topSeg.createLine();
  const bottomLine = bottomSeg.createLine();
  const leftLine = leftSeg.createLine();
  const rightLine = rightSeg.createLine();

  boxBoundingLines.push({
    seg: topSeg,
    line: topLine,
    rotatable: false,
    onlyAbsorbSeg: false,
    angle: getLineAngle(topLine),
    weight: BOX_BOUNDING_LINE_WEIGHT,
    bounds
  });
  boxBoundingLines.push({
    seg: bottomSeg,
    line: bottomLine,
    rotatable: false,
    onlyAbsorbSeg: false,
    angle: getLineAngle(bottomLine),
    weight: BOX_BOUNDING_LINE_WEIGHT,
    bounds
  });
  boxBoundingLines.push({
    seg: leftSeg,
    line: leftLine,
    rotatable: false,
    onlyAbsorbSeg: false,
    angle: getLineAngle(leftLine),
    weight: BOX_BOUNDING_LINE_WEIGHT,
    bounds
  });
  boxBoundingLines.push({
    seg: rightSeg,
    line: rightLine,
    rotatable: false,
    onlyAbsorbSeg: false,
    angle: getLineAngle(rightLine),
    weight: BOX_BOUNDING_LINE_WEIGHT,
    bounds
  });

  return { centerLines, boxBoundingLines };
}
